import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import products from "../data/products";
import "../assets/css/productDetails.css";

export default function ProductDetails() {
  const { slug } = useParams();
  const product = products.find((p) => p.slug === slug);
  const [selectedIndex, setSelectedIndex] = useState(0);
  if (!product) {
    return (
      <div className="container" style={{ padding: "6rem 0" }}>
        <h2>Product not found</h2>
        <p>We couldn't find the product you're looking for.</p>
        <Link to="/products" className="btn btn-primary" style={{ marginTop: "1rem" }}>
          Back to Products
        </Link>
      </div>
    );
  }

  // Use a banner image if product.banner exists, otherwise fall back to product.image
  const bannerImage = product.banner || product.image || "";
  const images = product.images && product.images.length ? product.images : [product.image];

  return (
    <>
       {/* Banner */}
        <section
          className="banner"
          style={{
            background: `
              linear-gradient(rgba(47,82,51,0.25), rgba(47,82,51,0.25)),
              url('${bannerImage}') no-repeat center/cover
            `,
          }}
        >

        <div className="container">
          <div className="banner-content">
            <h1>{product.name}</h1>
            <p>{product.description}</p>
            <a href="#product-details" className="btn btn-primary">Learn More</a>
          </div>
        </div>
      </section>

      {/* Product Details */}
      <section className="product-details" id="product-details">
        <div className="container">
          <div className="product-container">

            {/* Left: image gallery */}
            <div className="product-gallery">

              {/* Main Large Image (Zoomable) */} 
              <div
                className="main-image zoom-wrapper"
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  e.currentTarget.style.backgroundPosition = `${x}% ${y}%`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundPosition = "center";
                }}
                style={{
                  backgroundImage: `url(${images[selectedIndex]})`,
                }}
              >
                <img src={images[selectedIndex]} alt={product.name} />
              </div>


                {/* Thumbnails */}
                <div className="thumbnail-row">
                    {images.map((src, idx) => (
                    <button
                        key={idx}
                        className={`thumb ${idx === selectedIndex ? "active" : ""}`}
                        onClick={() => setSelectedIndex(idx)}
                    >
                        <img src={src} alt={`Thumbnail ${idx + 1}`} />
                    </button>
                    ))}
                </div>

                </div>


            {/* Right: info */}
            <div className="product-info">
              <div className="product-category">{product.category}</div>
              <h2>{product.name}</h2>

              <p className="product-description">
                {product.longDescription || product.description || "This product is formulated using natural ingredients for effective results."}
              </p>

              <div className="product-meta">
                <div className="meta-item">
                  <span className="meta-label">Category:</span>
                  <span className="meta-value">
                    {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Size:</span>
                  <span className="meta-value">{product.size || "50ml Tube"}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Ingredients:</span>
                  <span className="meta-value">{product.ingredients || "Natural Plant Extracts"}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Suitable for:</span>
                  <span className="meta-value">{product.suitableFor || "Sensitive Skin"}</span>
                </div>
              </div>

              <a 
                href="/assets/R_Vibes_Brochure.pdf" 
                download 
                className="btn btn-primary"
              >
                Download Brochure
              </a>
            </div>
          </div>
        </div>
      </section>

        {/* Features */}
        <section className="features-section">
            <div className="container">
                <div className="section-header">
                <h2>Key Features</h2>
                <p>Discover what makes {product.name} unique</p>
                </div>

                <div className="features-grid">
                {product.features?.map((feature, index) => (
                    <div className="feature-card" key={index}>
                    <div className="feature-icon">
                        <i className={`fa-solid ${feature.icon}`}></i>
                    </div>
                    <h3>{feature.title}</h3>
                    <p>{feature.text}</p>
                    </div>
                ))}
                </div>
            </div>
        </section>

        {/* Composition Section */}
        <section className="composition-section">
          <div className="container">
            <div className="section-header">
              <h2>Composition</h2>
              <p>Ingredients and their amounts used in {product.name}</p>
            </div>

            <div className="composition-table-wrapper">
              <table className="composition-table">
                <thead>
                  <tr>
                    <th>Ingredient</th>
                    <th>Amount</th>
                    <th>Ingredient</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows = [];
                    const list = product.composition || [];

                    for (let i = 0; i < list.length; i += 2) {
                      const first = list[i];
                      const second = list[i + 1];

                      rows.push(
                        <tr key={i}>
                          <td>{first.name}</td>
                          <td>{first.amount}</td>

                          {second ? (
                            <>
                              <td>{second.name}</td>
                              <td>{second.amount}</td>
                            </>
                          ) : (
                            <>
                              <td></td>
                              <td></td>
                            </>
                          )}
                        </tr>
                      );
                    }

                    return rows;
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </section>


        {/* How to Use */}
        <section className="how-to-use">
          <div className="container">
            <div className="section-header">
              <h2>How to Use</h2>
              <p>Follow these instructions for best results</p>
            </div>

            {/* If product has steps (cosmetics) */}
            {product.usage?.type === "steps" && (
              <div className="steps">
                {product.usage.steps.map((item, index) => (
                  <div className="step" key={index}>
                    <div className="step-number">{index + 1}</div>
                    <h3>{item.step}</h3>
                    <p>{item.description}</p>
                  </div>
                ))}
              </div>
            )}

            {/* If product has dosage (tablet/capsule/syrup) */}
            {product.usage?.type === "dosage" && (
              <div className="dosage-box">
                <ul>
                  {product.usage.dosage.map((line, index) => (
                    <li key={index}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* About the Product Section */}
        {product.about && (
          <section className="about-product">
            <div className="container">
              <div className="section-header">
                <h2>About {product.name}</h2>
                <p>{product.about}</p>
              </div>
            </div>
          </section>
        )}


      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Experience Relief?</h2>
          <p>Join thousands of satisfied customers who have found comfort with our products.</p>
          <Link to="/products" className="btn btn-primary">Explore More Products</Link>
        </div>
      </section>
    </>
  );
}
